import React from "react";
import { graphql } from "gatsby";
import PropTypes from "prop-types";
import { Col, Container, Row } from "react-bootstrap";
import classNames from "classnames";

import HeaderBanner from "./HeaderBanner";
import NavigationMenu from "./NavigationMenu";
import GatsbyImageWithSVGSupport from "./GatsbyImageWithSVGSupport";
import HeaderContent from "./HeaderContent";
import Card from "./Card";

import "../styles/components/Header.scss";

// Header container. Provide Header content as children of this component.
const Header = ({ location, header, banner, compact, children }) => {
    const activeKey = location?.pathname?.split("/")[1];

    return [
        <HeaderBanner key="header-banner" banner={banner} />,
        <NavigationMenu
            key="header-nav-menu"
            activeKey={activeKey}
            hasHeader={!!header}
        />,
        header && (
            <header
                className={classNames("header", header.className)}
                key="header-main"
            >
                {header?.heroImage && (
                    <div className="header-bg-container">
                        {!header.className?.includes("half-size-image") && (
                            <div className="blur-image" />
                        )}
                        {(header?.heroImage?.gatsbyImageData ||
                            header?.heroImage?.file) && (
                            <GatsbyImageWithSVGSupport
                                image={header.heroImage}
                                className="hero-image"
                                alt={
                                    header.heroImage.description ||
                                    header.heroImage.title ||
                                    "ShiftMed"
                                }
                            />
                        )}
                    </div>
                )}
                {header && (
                    <Container
                        className={classNames({
                            "header-content": header.heroImage,
                        })}
                    >
                        <div
                            className="header-main-content text-center text-md-start"
                            id="header-main-content"
                        >
                            <HeaderContent header={header} />
                            {children}
                        </div>
                        {header.headerCards?.length && (
                            <Row className="row-eq-height">
                                {header.headerCards.map(card => (
                                    <Col key={card.link} className="mb-5">
                                        <Card {...card} />
                                    </Col>
                                ))}
                            </Row>
                        )}
                    </Container>
                )}
            </header>
        ),
    ];
};

Header.propTypes = {
    header: PropTypes.shape({
        heroImage: PropTypes.shape({ gatsbyImageData: PropTypes.object }),
        className: PropTypes.string,
    }),
    children: PropTypes.node,
    banner: PropTypes.shape({
        content: PropTypes.shape({ raw: PropTypes.string }),
        linkText: PropTypes.string,
        linkTarget: PropTypes.string,
    }),
    compact: PropTypes.bool,
    location: PropTypes.object,
};

export const query = graphql`
    fragment HeaderData on ContentfulHeader {
        title {
            raw
        }
        copy {
            raw
        }
        className
        heroImage {
            gatsbyImageData(
                layout: CONSTRAINED
                placeholder: NONE
                formats: [PNG, WEBP]
            )
            title
            description
            file {
                contentType
                url
            }
            url
        }
        buttons {
            id
            label
            color
            url
            outlineStyle
            openNewTab
            disabled
        }
        form {
            formType
            variant
            lead {
                raw
            }
            successMessage {
                raw
            }
        }
        headerCards {
            title
            copy {
                raw
            }
            link
        }
    }
    fragment Banner on ContentfulBanner {
        linkText
        linkTarget
        text {
            raw
        }
    }
`;

export default Header;
